import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import { toastSuccess, toastError } from "../utils/toast";
import {
  CogIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  BellIcon,
  XMarkIcon,
  TrashIcon,
  ScaleIcon,
  NewspaperIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";

const envNewsEmail = process.env.REACT_APP_NEWS_ADMIN_EMAIL;
const NEWS_ADMIN_EMAIL =
  envNewsEmail && envNewsEmail.trim()
    ? envNewsEmail.trim().toLowerCase()
    : "info@advpro.it";
const NEWS_PAGE_SIZE = 5;

const Impostazioni = () => {
  const {
    user,
    acceptPrivacyPolicy,
    activeCongregazione,
    updateRapportoServizioFlag,
    delegheInfo,
  } = useAuth();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [cleanupStats, setCleanupStats] = useState({
    oldDisponibilitaCount: 0,
    loading: false,
  });
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(
    Boolean(user?.privacy_policy_accepted_at)
  );
  const [rapportoToggleLoading, setRapportoToggleLoading] = useState(false);
  const isNewsAdmin =
    user?.ruolo === "super_admin" &&
    user?.email?.toLowerCase() === NEWS_ADMIN_EMAIL;
  const [newsForm, setNewsForm] = useState({ titolo: "", contenuto: "" });
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsSaving, setNewsSaving] = useState(false);
  const [newsItems, setNewsItems] = useState([]);
  const [newsPage, setNewsPage] = useState(1);
  const [newsPagination, setNewsPagination] = useState({
    total: 0,
    totalPages: 0,
    pageSize: 5,
  });
  const rapportoEnabled = Boolean(
    activeCongregazione?.rapporto_servizio_attivo ??
      user?.congregazione_rapporto_servizio_attivo
  );
  const matchesCongregazione = (valueId, targetId) => {
    if (valueId == null || targetId == null) return false;
    return Number(valueId) === Number(targetId);
  };
  const activeCongregazioneId =
    activeCongregazione?.id || user?.congregazione_id || null;
  const isSuperAdmin = user?.ruolo === "super_admin";
  const isAdmin = user?.ruolo === "admin";
  const hasAdminDelega = Boolean(
    activeCongregazioneId &&
      delegheInfo?.deleghe?.some(
        (delega) =>
          matchesCongregazione(delega?.congregazione_id, activeCongregazioneId) &&
          delega?.ruolo_delega === "admin"
      )
  );
  const canModifyRapporto =
    Boolean(activeCongregazioneId) &&
    (isSuperAdmin ||
      (isAdmin &&
        (matchesCongregazione(activeCongregazioneId, user?.congregazione_id) ||
          hasAdminDelega)));
  const congregationIdForToggle = canModifyRapporto ? activeCongregazioneId : null;

  // Carica statistiche cleanup per super admin
  useEffect(() => {
    if (user?.ruolo === "super_admin") {
      fetchCleanupStats();
    }
  }, [user?.ruolo]);

  const fetchCleanupStats = async () => {
    setCleanupStats((prev) => ({ ...prev, loading: true }));
    try {
      const response = await api.get("/admin/cleanup-disponibilita/stats");
      setCleanupStats({
        oldDisponibilitaCount: response.data.oldDisponibilitaCount || 0,
        loading: false,
      });
    } catch (error) {
      console.error("Errore nel caricamento statistiche cleanup:", error);
      setCleanupStats((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    setPrivacyConsent(Boolean(user?.privacy_policy_accepted_at));
  }, [user?.privacy_policy_accepted_at]);

  const togglePrivacyConsent = async () => {
    if (privacyConsent) {
      toastError(
        "Per revocare il consenso visita la pagina Consensi Privacy."
      );
      return;
    }
    const result = await acceptPrivacyPolicy();
    if (result?.success) {
      setPrivacyConsent(true);
    }
  };

  const handleRapportoToggle = async () => {
    if (!congregationIdForToggle) {
      toastError("Seleziona una congregazione per gestire il rapporto.");
      return;
    }
    setRapportoToggleLoading(true);
    try {
      await api.put(
        `/rapporti-servizio/congregazioni/${congregationIdForToggle}/toggle`,
        {
          enabled: !rapportoEnabled,
        }
      );
      updateRapportoServizioFlag(!rapportoEnabled, congregationIdForToggle);
      toastSuccess(
        !rapportoEnabled
          ? "Rapporto di servizio attivato per la congregazione."
          : "Rapporto di servizio disattivato per la congregazione."
      );
    } catch (error) {
      console.error("Errore aggiornamento rapporto servizio:", error);
      toastError(
        error.response?.data?.message ||
          "Impossibile aggiornare il rapporto di servizio"
      );
    } finally {
      setRapportoToggleLoading(false);
    }
  };

  const fetchNews = useCallback(
    async (pageToLoad = 1) => {
      if (!isNewsAdmin) {
        return;
      }
      setNewsLoading(true);
      try {
        const response = await api.get(
          `/news?page=${pageToLoad}&limit=${NEWS_PAGE_SIZE}`
        );
        setNewsItems(response.data?.news || []);
        const pagination = response.data?.pagination || {};
        setNewsPagination({
          total: pagination.total || 0,
          totalPages: pagination.totalPages || 0,
          pageSize: NEWS_PAGE_SIZE,
        });
      } catch (error) {
        console.error("Errore nel caricamento delle news:", error);
        toastError(
          error.response?.data?.message ||
            "Impossibile caricare le news al momento"
        );
      } finally {
        setNewsLoading(false);
      }
    },
    [isNewsAdmin]
  );

  useEffect(() => {
    if (isNewsAdmin) {
      fetchNews(newsPage);
    }
  }, [fetchNews, isNewsAdmin, newsPage]);

  const handleNewsInputChange = (event) => {
    const { name, value } = event.target;
    setNewsForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleNewsSubmit = async (event) => {
    event.preventDefault();
    if (!newsForm.titolo.trim() || !newsForm.contenuto.trim()) {
      toastError("Compila titolo e contenuto per pubblicare una news");
      return;
    }

    setNewsSaving(true);
    try {
      await api.post("/news", {
        titolo: newsForm.titolo.trim(),
        contenuto: newsForm.contenuto.trim(),
      });
      toastSuccess("News pubblicata correttamente");
      setNewsForm({ titolo: "", contenuto: "" });
      setNewsPage(1);
      fetchNews(1);
    } catch (error) {
      console.error("Errore nella creazione della news:", error);
      toastError(
        error.response?.data?.message || "Impossibile pubblicare la news"
      );
    } finally {
      setNewsSaving(false);
    }
  };

  const handleDeleteNews = async (id) => {
    if (
      !window.confirm(
        "Sei sicuro di voler eliminare questa news? L'operazione non può essere annullata."
      )
    ) {
      return;
    }

    try {
      await api.delete(`/news/${id}`);
      toastSuccess("News eliminata");
      setNewsItems((prev) => prev.filter((item) => item.id !== id));
      if (newsItems.length <= 1 && newsPage > 1) {
        setNewsPage((prev) => Math.max(1, prev - 1));
      } else {
        fetchNews(newsPage);
      }
    } catch (error) {
      console.error("Errore nell'eliminazione della news:", error);
      toastError(
        error.response?.data?.message || "Impossibile eliminare la news"
      );
    }
  };

  const formatNewsDate = (value) => {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return "";
    }
    return parsed.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const handleCleanup = async () => {
    if (
      !window.confirm(
        `Sei sicuro di voler eliminare ${cleanupStats.oldDisponibilitaCount} disponibilità vecchie? Questa operazione non può essere annullata.`
      )
    ) {
      return;
    }

    setCleanupLoading(true);
    try {
      const response = await api.post(
        "/admin/cleanup-disponibilita?beforeCurrentMonth=true"
      );
      toastSuccess(
        response.data.message || `Cleanup completato: ${response.data.deletedCount} record eliminati.`
      );
      // Ricarica le statistiche
      await fetchCleanupStats();
    } catch (error) {
      console.error("Errore durante il cleanup:", error);
      toastError(
        error.response?.data?.message ||
          "Errore durante il cleanup delle disponibilità"
      );
    } finally {
      setCleanupLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <CogIcon className="h-8 w-8 text-primary-600 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900">Impostazioni</h1>
        </div>
        <p className="text-gray-600">
          Configura le impostazioni del sistema e del tuo account.
        </p>
        <div className="accent-line mt-4" />
      </div>

      <div className="space-y-6">
        {/* Informazioni Account */}
        <div className="bg-white shadow-lg rounded-lg p-6 relative overflow-hidden">
          <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
          <div className="flex items-center mb-4">
            <UserCircleIcon className="h-6 w-6 text-gray-600 mr-2" />
            <h2 className="text-xl font-semibold text-gray-900">
              Informazioni Account
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome Completo
              </label>
              <p className="text-gray-900">
                {user?.nome} {user?.cognome}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <p className="text-gray-900">{user?.email}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ruolo
              </label>
              <p className="text-gray-900 capitalize">
                {user?.ruolo || "volontario"}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Stato
              </label>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {user?.stato || "attivo"}
              </span>
            </div>
          </div>
        </div>

        {/* Sicurezza */}
        <div className="bg-white shadow-lg rounded-lg p-6 relative overflow-hidden">
          <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
          <div className="flex items-center mb-4">
            <ShieldCheckIcon className="h-6 w-6 text-gray-600 mr-2" />
            <h2 className="text-xl font-semibold text-gray-900">Sicurezza</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-900">
                  Cambia Password
                </h3>
                <p className="text-sm text-gray-500">
                  Aggiorna la tua password per mantenere sicuro l'account
                </p>
              </div>
              <button 
                onClick={() => setShowPasswordModal(true)}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors duration-200"
              >
                Modifica
              </button>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-900">
                  Sessioni Attive
                </h3>
                <p className="text-sm text-gray-500">
                  Gestisci i dispositivi connessi al tuo account
                </p>
              </div>
              <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-200">
                Visualizza
              </button>
            </div>
          </div>
        </div>

        {/* Rapporto di Servizio - visibile solo a admin e super admin */}
        {(user?.ruolo === "admin" || user?.ruolo === "super_admin") && (
          <div className="bg-white shadow-lg rounded-lg p-6 relative overflow-hidden">
            <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
            <div className="flex items-center mb-4">
              <BellIcon className="h-6 w-6 text-gray-600 mr-2" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Rapporto di Servizio
                </h2>
                <p className="text-sm text-gray-500">
                  Gestisci la raccolta dei rapporti per la congregazione.
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-gray-900">
                      Stato funzionalità
                    </h3>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                        rapportoEnabled
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {rapportoEnabled ? "Attivo" : "Disattivo"}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 max-w-xl mt-2">
                    {rapportoEnabled
                      ? "I proclamatori possono inserire e gestire i loro rapporti direttamente nella sezione “I miei turni”."
                      : "Attiva il rapporto di servizio per consentire alla congregazione di gestire ore, pubblicazioni e esperienze direttamente dall’app."}
                  </p>
                  {user?.ruolo === "super_admin" && !activeCongregazione?.id && (
                    <p className="text-xs text-amber-600 mt-2">
                      Seleziona una congregazione dal menu laterale per
                      modificare questa impostazione.
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {rapportoToggleLoading && (
                    <span className="text-xs text-gray-500">
                      Aggiornamento in corso...
                    </span>
                  )}
                  <label
                    className={`relative inline-flex items-center ${
                      canModifyRapporto ? "cursor-pointer" : "cursor-not-allowed"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={rapportoEnabled}
                      onChange={handleRapportoToggle}
                      disabled={!canModifyRapporto || rapportoToggleLoading}
                    />
                    <div
                      className={`relative w-11 h-6 bg-gray-200 rounded-full peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 transition peer-checked:bg-primary-600 ${
                        !canModifyRapporto || rapportoToggleLoading
                          ? "opacity-60"
                          : ""
                      }`}
                    >
                      <div
                        className={`absolute top-[2px] left-[2px] h-5 w-5 rounded-full bg-white shadow transition-transform ${
                          rapportoEnabled ? "translate-x-5" : ""
                        }`}
                      />
                    </div>
                  </label>
                </div>
              </div>
              {!canModifyRapporto && (
                <p className="text-xs text-gray-500">
                  Non hai i permessi o non è stata selezionata una congregazione per
                  modificare questo valore.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Consensi Privacy */}
        <div className="bg-white shadow-lg rounded-lg p-6 relative overflow-hidden">
          <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
          <div className="flex items-center mb-4">
            <ScaleIcon className="h-6 w-6 text-gray-600 mr-2" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Consensi Privacy
              </h2>
            </div>
          </div>
          <div className="flex items-center justify-between gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-900">
                Trattamento Dati Personali
              </h3>
              <p className="text-sm text-gray-500 max-w-md">
                Presto il mio consenso al trattamento dei miei dati personali in
                ottemperanza alla Normativa italiana e al GDPR (Regolamento UE
                2016/679).
              </p>
              <Link
                to="/consensi-privacy"
                className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium mt-2"
              >
                Scopri di più →
              </Link>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={privacyConsent}
                onChange={togglePrivacyConsent}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-primary-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
            </label>
          </div>
        </div>

        {isNewsAdmin && (
          <div className="bg-white shadow-lg rounded-lg p-6 relative overflow-hidden">
            <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
            <div className="flex items-center mb-4">
              <NewspaperIcon className="h-6 w-6 text-gray-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">News</h2>
            </div>

            <form onSubmit={handleNewsSubmit} className="space-y-4 mb-6">
              <div>
                <label
                  htmlFor="news-titolo"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Titolo
                </label>
                <input
                  id="news-titolo"
                  name="titolo"
                  type="text"
                  value={newsForm.titolo}
                  onChange={handleNewsInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Es. Rilascio nuova funzionalità"
                />
              </div>
              <div>
                <label
                  htmlFor="news-contenuto"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Contenuto
                </label>
                <textarea
                  id="news-contenuto"
                  name="contenuto"
                  rows="4"
                  value={newsForm.contenuto}
                  onChange={handleNewsInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  placeholder="Descrivi la novità o le istruzioni importanti..."
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={newsSaving}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors duration-200 disabled:opacity-50"
                >
                  {newsSaving ? "Pubblicazione..." : "Pubblica News"}
                </button>
              </div>
            </form>

            <div className="space-y-4">
              {newsLoading ? (
                <div className="flex items-center justify-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mr-3"></div>
                  <span className="text-gray-600">Caricamento news...</span>
                </div>
              ) : newsItems.length === 0 ? (
                <p className="text-gray-500 text-center">
                  Nessuna news pubblicata
                </p>
              ) : (
                newsItems.map((item) => (
                  <div
                    key={item.id}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {item.titolo}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {formatNewsDate(item.created_at)}{" "}
                          {item.author_nome || item.author_email
                            ? `• ${item.author_nome || item.author_email}`
                            : ""}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteNews(item.id)}
                        className="text-sm text-red-600 hover:text-red-700 flex items-center"
                      >
                        <TrashIcon className="h-4 w-4 mr-1" />
                        Elimina
                      </button>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-line mt-3">
                      {item.contenuto}
                    </p>
                  </div>
                ))
              )}
            </div>

            {newsPagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <button
                  type="button"
                  onClick={() =>
                    setNewsPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={newsPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
                >
                  ← Precedenti
                </button>
                <p className="text-sm text-gray-600">
                  Pagina {newsPage} di {newsPagination.totalPages}
                </p>
                <button
                  type="button"
                  onClick={() =>
                    setNewsPage((prev) =>
                      newsPagination.totalPages
                        ? Math.min(newsPagination.totalPages, prev + 1)
                        : prev + 1
                    )
                  }
                  disabled={
                    newsPagination.totalPages > 0
                      ? newsPage >= newsPagination.totalPages
                      : newsItems.length < NEWS_PAGE_SIZE
                  }
                  className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
                >
                  Successive →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Utility - Solo Super Admin */}
        {user?.ruolo === "super_admin" && (
          <div className="bg-white shadow-lg rounded-lg p-6 relative overflow-hidden">
            <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
            <div className="flex items-center mb-4">
              <WrenchScrewdriverIcon className="h-6 w-6 text-gray-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">Utility</h2>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-sm font-medium text-gray-900">
                      Cleanup Disponibilità
                    </h3>
                    {cleanupStats.loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {cleanupStats.oldDisponibilitaCount} disponibilità vecchie
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">
                    Elimina le disponibilità di mesi precedenti per mantenere il database pulito
                  </p>
                </div>
                <button
                  onClick={handleCleanup}
                  disabled={cleanupLoading || cleanupStats.oldDisponibilitaCount === 0}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {cleanupLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Eliminazione...</span>
                    </>
                  ) : (
                    <>
                      <TrashIcon className="h-4 w-4" />
                      <span>Cleanup</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sistema */}
        {user?.ruolo === "admin" && (
          <div className="bg-white shadow-lg rounded-lg p-6 relative overflow-hidden">
            <span className="accent-bar top-6 bottom-6" aria-hidden="true" />
            <div className="flex items-center mb-4">
              <CogIcon className="h-6 w-6 text-gray-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">
                Configurazione Sistema
              </h2>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">
                    Backup Database
                  </h3>
                  <p className="text-sm text-gray-500">
                    Esegui backup manuale del database
                  </p>
                </div>
                <button className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors duration-200">
                  Esegui Backup
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">
                    Log Sistema
                  </h3>
                  <p className="text-sm text-gray-500">
                    Visualizza i log di sistema e errori
                  </p>
                </div>
                <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors duration-200">
                  Visualizza Log
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal Cambio Password */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Cambia Password
                </h3>
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordData({
                      currentPassword: "",
                      newPassword: "",
                      confirmPassword: "",
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  
                  // Validazione
                  if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
                    toastError("Tutti i campi sono obbligatori");
                    return;
                  }

                  if (passwordData.newPassword.length < 6) {
                    toastError("La nuova password deve essere di almeno 6 caratteri");
                    return;
                  }

                  if (passwordData.newPassword !== passwordData.confirmPassword) {
                    toastError("Le password non corrispondono");
                    return;
                  }

                  if (passwordData.currentPassword === passwordData.newPassword) {
                    toastError("La nuova password deve essere diversa dalla password corrente");
                    return;
                  }

                  setLoading(true);
                  try {
                    await api.put("/auth/change-password", {
                      currentPassword: passwordData.currentPassword,
                      newPassword: passwordData.newPassword,
                    });

                    toastSuccess("Password cambiata con successo!");
                    setShowPasswordModal(false);
                    setPasswordData({
                      currentPassword: "",
                      newPassword: "",
                      confirmPassword: "",
                    });
                  } catch (error) {
                    console.error("Errore nel cambio password:", error);
                    toastError(
                      error.response?.data?.message || "Errore durante il cambio password"
                    );
                  } finally {
                    setLoading(false);
                  }
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password Corrente *
                  </label>
                  <input
                    type="password"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={passwordData.currentPassword}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        currentPassword: e.target.value,
                      })
                    }
                    placeholder="Inserisci la password corrente"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nuova Password *
                  </label>
                  <input
                    type="password"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={passwordData.newPassword}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        newPassword: e.target.value,
                      })
                    }
                    placeholder="Minimo 6 caratteri"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    La password deve contenere almeno 6 caratteri
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Conferma Nuova Password *
                  </label>
                  <input
                    type="password"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={passwordData.confirmPassword}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        confirmPassword: e.target.value,
                      })
                    }
                    placeholder="Ripeti la nuova password"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordModal(false);
                      setPasswordData({
                        currentPassword: "",
                        newPassword: "",
                        confirmPassword: "",
                      });
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    disabled={loading}
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Salvataggio..." : "Salva"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Impostazioni;
